import React from "react";
import { Share2 } from "lucide-react";
import ScoutAvatar from "./ScoutAvatar";
import { NewPill, SourceLabelPill, OutsideFramePill, OnRadarPill } from "./ScoutPills";
import { formatScoutDate } from "../../utils/scoutFormat";
import { formatScoutPosition } from "../../constants/scoutRequestChoices";

// A single recommendation in the club inbox (design 1d). Card state comes from the
// server's club_status (new | on_radar | dismissed): "new" shows exactly two
// actions (Dismiss / Add to radar); "on_radar" shows the green pill + Undo; and
// "dismissed" fades the card + Undo. The "Outside fee frame" pill renders ONLY when
// outside_fee_frame === true (null/false → no pill). `isNew` drives the highlight.
const playerMeta = (rec) =>
  [formatScoutPosition(rec.player_position), rec.player_age, rec.player_club]
    .filter((v) => v !== null && v !== undefined && v !== "")
    .join(" · ");

const RecommendationCard = ({ rec, isNew, onAdd, onUndoAdd, onDismiss, onUndoDismiss, onShare, busy }) => {
  const attribution = `${rec.recommended_by?.agent || "Former member"}${
    rec.recommended_by?.agency ? ` · ${rec.recommended_by.agency}` : ""
  } · ${formatScoutDate(rec.recommended_by?.at || rec.created_at)}`;

  const onRadar = rec.club_status === "on_radar";
  const dismissed = rec.club_status === "dismissed";
  const showNew = isNew && !onRadar && !dismissed;

  return (
    <div
      className={`bg-white rounded-xl p-5 border shadow-[0_1px_2px_rgba(17,24,39,0.04)] transition ${
        dismissed ? "opacity-50" : ""
      } ${showNew ? "border-[#BFDBFE]" : "border-[#E5E7EB]"}`}
      data-testid="recommendation-card"
      data-status={rec.club_status}
    >
      <div className="flex items-start gap-3">
        <ScoutAvatar name={rec.player_name} size={40} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[15px] font-semibold text-[#111827]">{rec.player_name}</p>
            {showNew && <NewPill />}
            {rec.outside_fee_frame === true && <OutsideFramePill />}
            <SourceLabelPill label={rec.source_label} />
          </div>
          <p className="text-[12.5px] text-[#6B7280] mt-0.5">{playerMeta(rec)}</p>
        </div>
      </div>

      {rec.reason && <p className="text-[13px] italic text-[#374151] mt-3">&ldquo;{rec.reason}&rdquo;</p>}

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#F3F4F6] gap-3">
        <span className="text-[11.5px] text-[#9CA3AF] truncate">{attribution}</span>

        {onRadar ? (
          <div className="flex items-center gap-3 flex-shrink-0">
            <OnRadarPill />
            <button
              onClick={() => onUndoAdd(rec)}
              className="text-[12.5px] font-semibold text-[#1B6FE0] hover:underline"
            >
              Undo
            </button>
          </div>
        ) : dismissed ? (
          <button
            onClick={() => onUndoDismiss(rec)}
            className="text-[12.5px] font-semibold text-[#1B6FE0] hover:underline flex-shrink-0"
          >
            Undo
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-shrink-0">
            {onShare && (
              <button
                onClick={() => onShare(rec)}
                title="Forward into chat"
                aria-label="Forward into chat"
                data-testid="forward-recommendation"
                className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-[#6B7280] hover:text-[#111827] hover:bg-gray-50"
              >
                <Share2 className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => onDismiss(rec)}
              disabled={busy}
              className="min-h-[44px] px-3.5 py-2 rounded-lg text-[12.5px] font-semibold text-[#6B7280] hover:text-[#111827] hover:bg-gray-50"
            >
              Dismiss
            </button>
            <button
              onClick={() => onAdd(rec)}
              disabled={busy}
              className="min-h-[44px] px-3.5 py-2 rounded-lg bg-[#2283FF] text-white text-[12.5px] font-semibold hover:bg-[#1B6FE0] disabled:opacity-50"
            >
              Add to radar
            </button>
          </div>
        )}
      </div>

      {onRadar && (
        <p className="text-[11.5px] text-[#16A34A] mt-2">
          {rec.recommended_by?.agent || "The agent"} now sees &ldquo;On the radar&rdquo;. Their
          recommendation stays credited.
        </p>
      )}
      {dismissed && (
        <p className="text-[11.5px] text-[#9CA3AF] mt-2">
          Dismissed: silent, the agent is never notified.
        </p>
      )}
    </div>
  );
};

export default RecommendationCard;
